// ============================================================
// useLiveTopics.ts — (D12 live updates) "nobody should ever have to refresh
// to see someone else's change". Mount ONCE (App).
//
// Turns the server's data-free live messages (services/presence) into the
// app's change buses, so every screen re-reads through its normal APIs:
//   • case-changed { caseId, topics, actorUserId } → the store-backed parts
//     of the open case re-read (hooks/liveCaseRefresh) + emitCaseChange(
//     caseId, topicKinds(topics), { remote, actorUserId, topics }) for the
//     sections that load their own data (utils/caseChangeEvents);
//   • firm-changed { topics } → emitFirmChange (firm lists / dashboards);
//   • user-changed { topics } → emitUserChange (the bell).
// A burst is coalesced: one flush per DEBOUNCE_MS (topics merged per case).
// This tab's own changes never arrive (presence drops originTab === TAB_ID).
//
// Catch-up — "refetch what's on screen" (the open case: every part; every
// firm list; the bell) — after: a socket RE-connect, a `live-resync` (the
// server's relay reconnected), and the tab becoming visible again after
// VISIBLE_RESYNC_AFTER_MS hidden (at once if the socket is down).
//
// Fallback while the socket is down: GET /api/live/stamp?caseId=<open case>
// every STAMP_POLL_MS while the tab is visible; a moved `at` → catch up.
// ============================================================
import { useEffect } from 'react'
import { presence } from '../services/presence'
import { useCasevoStore } from '../store/useCasevoStore'
import { getLiveStampApi, getMeApi } from '../api/casevoApi'
import { emitCaseChange, topicKinds, ALL_CASE_CHANGE_KINDS } from '../utils/caseChangeEvents'
import { emitFirmChange, emitUserChange, ALL_FIRM_TOPICS, ALL_USER_TOPICS } from '../utils/liveEvents'
import { refreshCaseStoreForTopics, refreshOpenCase, refreshTpiContacts } from './liveCaseRefresh'

export const DEBOUNCE_MS = 300
export const STAMP_POLL_MS = 30_000
export const STAMP_BASELINE_MS = 5_000
export const VISIBLE_RESYNC_AFTER_MS = 30_000

const isVisible = (): boolean => typeof document === 'undefined' || document.visibilityState !== 'hidden'

// Store-backed firm / user data (no component owns it): re-read here.
//   firm users → the firm users slice (names, avatars, who's active);
//   firm settings → the TPI contact directory, and (big-audit fix batch 2)
//     the firm's trading name in the app shell (an admin may have renamed it);
//   user favourites → the sidebar pins; user preferences → the colour scheme
//   (Settings → Microsoft re-reads its own status on the bus).
export const refreshStoreForTopics = (firm: readonly string[], user: readonly string[]): void => {
  const st = useCasevoStore.getState()
  if (firm.includes('users') && st.firmUsersLoaded) void st.loadFirmUsers(true)
  if (firm.includes('settings')) { void refreshTpiContacts(); void refreshFirmName() }
  if (user.includes('favourites')) void st.loadFavourites()
  if (user.includes('preferences')) void st.loadColourSchemes()
}

// (Big-audit fix batch 2) The signed-in user's firm name (authUser.firmName)
// — re-read from /auth/me; only the name is taken (nothing else is replaced).
export const refreshFirmName = async (): Promise<void> => {
  try {
    const me = await getMeApi()
    const st = useCasevoStore.getState()
    if (st.authUser && me?.firmName !== undefined && me.firmName !== st.authUser.firmName) st.setAuthUser({ ...st.authUser, firmName: me.firmName })
  } catch { /* keep what's shown */ }
}

// Everything on screen re-reads.
export const resyncEverything = (): void => {
  const caseId = useCasevoStore.getState().currentCase?.id
  if (caseId) resyncCase(caseId)
  refreshStoreForTopics(['users'], ['favourites', 'preferences'])
  emitFirmChange(ALL_FIRM_TOPICS, { resync: true })
  emitUserChange(ALL_USER_TOPICS, { resync: true })
}
const resyncCase = (caseId: string): void => {
  void refreshOpenCase(caseId)
  emitCaseChange(caseId, ALL_CASE_CHANGE_KINDS, { remote: true, resync: true })
}

export const useLiveTopics = (): void => {
  const signedIn = useCasevoStore((s) => s.isAuthenticated && !!s.authUser)

  // ── The socket's messages + catch-ups, coalesced ──
  useEffect(() => {
    if (!signedIn) return undefined
    const cases = new Map<string, { topics: Set<string>; actor: string | null }>()
    const firm = { topics: new Set<string>(), actor: null as string | null }
    const user = new Set<string>()
    let resync = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const flush = (): void => {
      timer = null
      if (resync) {
        resync = false
        cases.clear(); firm.topics.clear(); user.clear()
        resyncEverything()
        return
      }
      const cs = Array.from(cases.entries())
      cases.clear()
      for (const [caseId, c] of cs) {
        const topics = Array.from(c.topics)
        refreshCaseStoreForTopics(caseId, topics)
        emitCaseChange(caseId, topicKinds(topics), { remote: true, actorUserId: c.actor, topics })
      }
      const firmTopics = Array.from(firm.topics)
      const userTopics = Array.from(user)
      const actor = firm.actor
      firm.topics.clear(); firm.actor = null; user.clear()
      refreshStoreForTopics(firmTopics, userTopics)
      if (firmTopics.length) emitFirmChange(firmTopics, { actorUserId: actor })
      if (userTopics.length) emitUserChange(userTopics)
    }
    const schedule = (): void => { if (!timer) timer = setTimeout(flush, DEBOUNCE_MS) }
    const catchUp = (): void => { resync = true; schedule() }

    let hiddenAt: number | null = isVisible() ? null : Date.now()
    const onVisibility = (): void => {
      if (!isVisible()) { if (hiddenAt === null) hiddenAt = Date.now(); return }
      const away = hiddenAt === null ? 0 : Date.now() - hiddenAt
      hiddenAt = null
      if (away >= VISIBLE_RESYNC_AFTER_MS || (away > 0 && !presence.isConnected())) catchUp()
    }
    document.addEventListener('visibilitychange', onVisibility)

    const offs = [
      presence.subscribeCaseChanged((e) => {
        const c = cases.get(e.caseId) ?? { topics: new Set<string>(), actor: null }
        e.topics.forEach((t) => c.topics.add(t))
        if (e.actorUserId !== undefined) c.actor = e.actorUserId ?? c.actor
        cases.set(e.caseId, c)
        schedule()
      }),
      presence.subscribeFirmChanged((e) => {
        e.topics.forEach((t) => firm.topics.add(t))
        firm.actor = e.actorUserId ?? firm.actor
        schedule()
      }),
      presence.subscribeUserChanged((e) => { e.topics.forEach((t) => user.add(t)); schedule() }),
      presence.subscribeLiveResync(catchUp),
      presence.subscribeReconnected(catchUp),
    ]
    return () => {
      offs.forEach((off) => off())
      document.removeEventListener('visibilitychange', onVisibility)
      if (timer) clearTimeout(timer)
    }
  }, [signedIn])

  // ── Fallback: poll the change stamp while the socket is down ──
  useEffect(() => {
    if (!signedIn) return undefined
    let poll: ReturnType<typeof setInterval> | null = null
    let seen: { caseId: string | null; caseAt: string | null; firmAt: string | null } | null = null
    let busy = false
    const tick = async (): Promise<void> => {
      if (busy || presence.isConnected() || !isVisible()) return
      busy = true
      try {
        const caseId = useCasevoStore.getState().currentCase?.id ?? null
        const s = await getLiveStampApi(caseId)
        if (presence.isConnected()) return // came back meanwhile — the reconnect catches up
        const now = { caseId, caseAt: s.case?.at ?? null, firmAt: s.firm?.at ?? null }
        const prev = seen
        seen = now
        if (!prev) return // the first answer is the baseline
        if (prev.caseId === caseId && caseId && now.caseAt && now.caseAt !== prev.caseAt) resyncCase(caseId)
        if (now.firmAt && now.firmAt !== prev.firmAt) emitFirmChange(ALL_FIRM_TOPICS, { resync: true })
      } catch { /* try again next time */ } finally { busy = false }
    }
    // The baseline is taken shortly after the socket goes down (not at once:
    // at sign-in the socket is "down" for the moment it takes to connect).
    let first: ReturnType<typeof setTimeout> | null = null
    const start = (): void => {
      if (poll) return
      seen = null
      first = setTimeout(() => { first = null; void tick() }, STAMP_BASELINE_MS)
      poll = setInterval(() => { void tick() }, STAMP_POLL_MS)
    }
    const stop = (): void => {
      if (first) { clearTimeout(first); first = null }
      if (poll) { clearInterval(poll); poll = null }
    }
    const off = presence.subscribeConnection((up) => { if (up) stop(); else start() })
    return () => { off(); stop() }
  }, [signedIn])
}

export default useLiveTopics
